// src/context/BillingContext.js
//
// Provider billing per Master HR.
// Stessa logica del gestionale clinico ma adattato per:
//   - useUser() invece di useAuth()
//   - activeCenterId invece di centerId
//   - Import da ../firebase invece di ../services/firebase

import React, { createContext, useContext, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../UserContext";

const BillingContext = createContext();

export function useBilling() {
  return useContext(BillingContext);
}

const DEFAULT_FEATURES = {
  dicomEnabled: true, crmEnabled: true, whatsappEnabled: true,
  maxStaff: -1, maxDoctors: -1,
};

const DEFAULT_SUBSCRIPTION = { status: "active", planId: null };

const DEFAULT_SMS_QUOTA = {
  includedTotal: 0, includedUsed: 0, rolloverBalance: 0,
  walletBalance: 0, overdraftCount: 0, overdraftTotalCost: 0,
  totalSentThisMonth: 0,
};

export function BillingProvider({ children }) {
  const { activeCenterId } = useUser();
  const [subscription, setSubscription] = useState(DEFAULT_SUBSCRIPTION);
  const [smsQuota, setSmsQuota] = useState(DEFAULT_SMS_QUOTA);
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [billingLoading, setBillingLoading] = useState(true);

  useEffect(() => {
    if (!activeCenterId) { setSubscription(DEFAULT_SUBSCRIPTION); return; }
    const unsub = onSnapshot(
      doc(db, "centers", activeCenterId, "billing", "subscription"),
      (snap) => setSubscription(snap.exists() ? snap.data() : DEFAULT_SUBSCRIPTION),
      () => setSubscription(DEFAULT_SUBSCRIPTION)
    );
    return () => unsub();
  }, [activeCenterId]);

  useEffect(() => {
    if (!activeCenterId) { setFeatures(DEFAULT_FEATURES); return; }
    const unsub = onSnapshot(
      doc(db, "centers", activeCenterId, "billing", "features"),
      (snap) => setFeatures(snap.exists() ? snap.data() : DEFAULT_FEATURES),
      () => setFeatures(DEFAULT_FEATURES)
    );
    return () => unsub();
  }, [activeCenterId]);

  useEffect(() => {
    if (!activeCenterId) { setSmsQuota(DEFAULT_SMS_QUOTA); setBillingLoading(false); return; }
    const unsub = onSnapshot(
      doc(db, "centers", activeCenterId, "billing", "sms_quota"),
      (snap) => { setSmsQuota(snap.exists() ? snap.data() : DEFAULT_SMS_QUOTA); setBillingLoading(false); },
      () => { setSmsQuota(DEFAULT_SMS_QUOTA); setBillingLoading(false); }
    );
    return () => unsub();
  }, [activeCenterId]);

  const isFeatureOn = (name) => {
    if (features.overrides?.[name] !== null && features.overrides?.[name] !== undefined) {
      return features.overrides[name];
    }
    return features[name] ?? true;
  };

  const isSuspended = subscription.status === "suspended";
  const isPastDue = subscription.status === "past_due";
  const isActive = subscription.status === "active" || subscription.status === "trialing";

  const remainingSms = Math.max(0,
    (smsQuota.includedTotal || 0) - (smsQuota.includedUsed || 0)
  ) + (smsQuota.rolloverBalance || 0) + (smsQuota.walletBalance || 0);

  const smsUsagePercent = smsQuota.includedTotal > 0
    ? Math.min(100, Math.round((smsQuota.includedUsed / smsQuota.includedTotal) * 100))
    : 0;

  const value = {
    subscription, smsQuota, features, billingLoading,
    isFeatureOn, isSuspended, isPastDue, isActive,
    remainingSms, smsUsagePercent,
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
}